import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Button,
  Paper,
  TableContainer,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Avatar,
  LinearProgress,
  Pagination,
  alpha,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Search,
  FilterList,
  Refresh,
  Visibility,
  Download,
  MoreVert,
  CheckCircle,
  Cancel,
  LockClock,
  TaskAlt,
  Warning,
} from "@mui/icons-material";
import axios from "axios";
import React, { useEffect, useState } from "react";
 import { IonPage } from "@ionic/react";
import { requestID, baseUrl, config } from "../../../common/utils";

const ManagementPage = () => {
  const theme = useTheme();
  const [cases, setCases] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Modal state
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);

  const handleViewMore = (message) => {
    setSelectedMessage(message);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedMessage(null);
  };

  const handleMenuOpen = (event, caseItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedCase(caseItem);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCase(null);
  };

  const fetchStatus = async () => {
    try {
      const payload = { requestID: requestID() };
      const res = await axios.post(
        `${baseUrl()}whitsleBlowings/getAllBlowingCase`,
        payload,
        config
      );
      setCases(res.data.data);
    } catch (error) {
      console.error("Error fetching cases:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleAction = async (caseItem, actionType) => {
    let confirmMessage = "";
    let payload = {};

    if (actionType === "accept") {
      confirmMessage = "Are you sure you want to accept this case?";
      payload = {
        requestID: requestID(),
        status: "investigated",
        whitsle_id: caseItem.whitsle_id,
      };
    } else if (actionType === "close") {
      confirmMessage = "Are you sure you want to close this case?";
      payload = {
        requestID: requestID(),
        status: "closed",
        whitsle_id: caseItem.whitsle_id,
      };
    }

    if (window.confirm(confirmMessage)) {
      try {
        await axios.post(
          `${baseUrl()}whitsleBlowings/updateWhistleBlowingStatus`,
          payload,
          config
        );
        fetchStatus();
      } catch (error) {
        console.error("Error performing action:", error);
      }
    }
    handleMenuClose();
  };

  const handleReject = async (caseItem) => {
    if (window.confirm("Reject this case? This action cannot be undone.")) {
      try {
        const payload = {
          whitsle_id: caseItem.whitsle_id,
          status: "rejected",
          requestID: requestID(),
        };
        await axios.post(
          `${baseUrl()}whitsleBlowings/rejectWhistleBlowingStatus`,
          payload,
          config
        );
        alert("Case rejected successfully.");
        fetchStatus();
      } catch (error) {
        console.error("Failed to close case:", error);
        alert("Something went wrong!");
      }
    }
    handleMenuClose();
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      case "investigated":
        return "info";
      case "closed":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <LockClock />;
      case "rejected":
        return <Cancel />;
      case "investigated":
        return <Search />;
      case "closed":
        return <TaskAlt />;
      default:
        return <Warning />;
    }
  };

  const filteredCases = cases?.filter(
    (c) =>
      c.case_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.fullname?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const pageCount = Math.ceil(filteredCases?.length / rowsPerPage);
  const paginatedCases = filteredCases?.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handleRefresh = () => {
    setRefreshing(true);
    setPage(1); // Reset to first page on refresh
    fetchStatus();
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top when page changes
    window.scrollTo(0, 0);
  };

  // Reset to first page when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Status counts for dashboard
  const statusCounts = cases?.reduce((acc, curr) => {
    acc[curr?.status] = (acc[curr?.status] || 0) + 1;
    return acc;
  }, {});

  const statusCards = [
    {
      status: "pending",
      label: "Pending",
      color: "warning",
      icon: <LockClock />,
    },
    {
      status: "investigated",
      label: "Investigated",
      color: "info",
      icon: <Search />,
    },
    { status: "closed", label: "Closed", color: "success", icon: <TaskAlt /> },
    { status: "rejected", label: "Rejected", color: "error", icon: <Cancel /> },
  ];

  return (
    <IonPage className="pushDown">
      <Box
        sx={{
          p: 3,
          backgroundColor: "#f9fafb",
          minHeight: "100vh",
          overflow: "scroll",
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.main,
                  width: 56,
                  height: 56,
                }}
              >
                <TaskAlt sx={{ fontSize: 30 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight="700" color="primary">
                  Case Management
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage all whistleblower cases
                </Typography>
              </Box>
            </Box>

            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              startIcon={<Refresh />}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Refresh
            </Button>
          </Box>

          {/* Status Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {statusCards.map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.status}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette[card.color].main,
                      0.1
                    )} 0%, ${alpha(
                      theme.palette[card.color].light,
                      0.2
                    )} 100%)`,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h3"
                          fontWeight="700"
                          color={`${card?.color}.main`}
                        >
                          {statusCounts[card.status] || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {card?.label}
                        </Typography>
                      </Box>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme?.palette[card?.color].main, 0.2),
                          width: 48,
                          height: 48,
                        }}
                      >
                        {React.cloneElement(card?.icon, {
                          sx: { color: theme?.palette[card?.color].main },
                        })}
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Search and Filter Bar */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              mb: 3,
              background: "white",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                placeholder="Search cases or names..."
                variant="outlined"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2 },
                }}
              />
              <Button
                startIcon={<FilterList />}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Filters
              </Button>
            </Box>
          </Paper>
        </Box>

        {loading ? (
          <Box>
            {[1, 2, 3, 4, 5].map((item) => (
              <LinearProgress
                key={item}
                sx={{ mb: 1, borderRadius: 3, height: 8 }}
              />
            ))}
          </Box>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: `1px solid ${theme.palette.divider}`,
                background: "white",
                mb: 2,
              }}
            >
              {refreshing && <LinearProgress color="primary" />}
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: "700",
                          background: alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        Date
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "700",
                          background: alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "700",
                          background: alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        Message
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "700",
                          background: alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        Documents
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "700",
                          background: alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        Case
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "700",
                          background: alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "700",
                          background: alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedCases.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              variant="h6"
                              color="text.secondary"
                              gutterBottom
                            >
                              No cases found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {searchTerm
                                ? "Try adjusting your search terms"
                                : "No cases have been reported yet"}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedCases.map((c) => (
                        <TableRow
                          key={c.id}
                          hover
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                            transition: "background-color 0.2s",
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.03
                              ),
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {new Date(c.created_at).toLocaleDateString()}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {new Date(c.created_at).toLocaleTimeString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {c.fullname}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "pre-line" }}>
                            {c.messages.length > 50 ? (
                              <>
                                {c.messages.substring(0, 50)}...
                                <Box
                                  component="span"
                                  onClick={() => handleViewMore(c.messages)}
                                  sx={{
                                    color: "primary.main",
                                    cursor: "pointer",
                                    ml: 1,
                                    fontWeight: 500,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Visibility sx={{ fontSize: 16 }} /> View More
                                </Box>
                              </>
                            ) : (
                              c.messages
                            )}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              try {
                                const files = JSON.parse(c.proof);
                                if (Array.isArray(files) && files.length > 0) {
                                  return files.map((file, i) => {
                                    const cleanedPath = file.replace(/\\/g, "");
                                    const fileName = cleanedPath
                                      .split("/")
                                      .pop();
                                    return (
                                      <Box
                                        key={i}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          mb: i < files.length - 1 ? 1 : 0,
                                        }}
                                      >
                                        <Download
                                          sx={{
                                            fontSize: 16,
                                            color: "primary.main",
                                          }}
                                        />
                                        <a
                                          href={`${baseUrl()}${cleanedPath}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            textDecoration: "none",
                                            color: theme.palette.primary.main,
                                            wordBreak: "break-word",
                                            fontSize: "0.875rem",
                                          }}
                                        >
                                          {fileName}
                                        </a>
                                      </Box>
                                    );
                                  });
                                } else {
                                  return (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      No documents
                                    </Typography>
                                  );
                                }
                              } catch {
                                return (
                                  <Typography variant="body2" color="error">
                                    Invalid document
                                  </Typography>
                                );
                              }
                            })()}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {c.case_name || "Unnamed Case"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(c.status)}
                              label={
                                c.status
                                  ? c.status.charAt(0).toUpperCase() +
                                    c.status.slice(1)
                                  : "Unknown"
                              }
                              color={getStatusColor(c.status)}
                              variant="outlined"
                              sx={{ fontWeight: 600, borderRadius: 1 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Actions">
                              <IconButton
                                onClick={(e) => handleMenuOpen(e, c)}
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: 2,
                                }}
                              >
                                <MoreVert />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Pagination */}
            {filteredCases.length > rowsPerPage && (
              <Box
                sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 2 }}
              >
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      borderRadius: 2,
                      fontWeight: 600,
                    },
                  }}
                />
              </Box>
            )}

            {/* Results count */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", mb: 3 }}
            >
              Showing {paginatedCases.length} of {filteredCases.length} cases
              {searchTerm && " (filtered)"}
            </Typography>
          </>
        )}

        {/* Message Modal */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3 },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            Case Message Details
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <TextField
              value={selectedMessage}
              multiline
              fullWidth
              minRows={6}
              variant="outlined"
              InputProps={{
                readOnly: true,
                sx: { borderRadius: 2 },
              }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseModal}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 200,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            },
          }}
        >
          {selectedCase?.status === "pending" && (
            <MenuItem
              onClick={() => handleAction(selectedCase, "accept")}
              sx={{ color: "info.main" }}
            >
              <CheckCircle sx={{ mr: 1 }} /> Accept Case
            </MenuItem>
          )}
          {selectedCase?.status === "investigated" && (
            <MenuItem
              onClick={() => handleAction(selectedCase, "close")}
              sx={{ color: "success.main" }}
            >
              <TaskAlt sx={{ mr: 1 }} /> Close Case
            </MenuItem>
          )}
          {selectedCase?.status === "pending" && (
            <MenuItem
              onClick={() => handleReject(selectedCase)}
              sx={{ color: "error.main" }}
            >
              <Cancel sx={{ mr: 1 }} /> Reject Case
            </MenuItem>
          )}
          <MenuItem onClick={handleMenuClose}>Cancel</MenuItem>
        </Menu>
      </Box>
    </IonPage>
  );
};

export default ManagementPage;
